import React from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Lightbulb, TrendingDown, TrendingUp, Flame, Target, Dumbbell } from 'lucide-react'
import { Card, CardContent } from '../../../../components/ui/card'
import { cn } from '../../../../lib/utils'

const ICON_BY_ID = {
  weeklyChangeLosing: TrendingDown,
  weeklyChangeGaining: TrendingUp,
  kcalAvg: Target,
  trainingRestKcal: Flame,
  kcalBurnedAvg: Dumbbell,
  mealStreak: Flame,
  lowProtein: TrendingDown,
  highProtein: TrendingUp,
}

// Trans <0/> placeholders resolve to bidi-isolated emphasis spans. <bdi>
// keeps each numeric chunk (e.g. "+200 kcal") cohesive when embedded in
// an Arabic RTL sentence, and the bold/orange styling makes the metric
// jump out from the surrounding prose.
const INSIGHT_COMPONENTS = [
  <bdi className="font-semibold text-foreground" />,
]

const ACCENT_BY_SEVERITY = {
  positive: 'text-emerald-500',
  negative: 'text-amber-500',
  neutral: 'text-app-primary-500',
}

export function InsightsFeed({ insights }) {
  const { t } = useTranslation()

  if (!insights || insights.length === 0) {
    return (
      <Card>
        <CardContent className="p-4 flex items-center gap-3 text-sm text-muted-foreground">
          <Lightbulb className="w-5 h-5 text-muted-foreground/60 flex-shrink-0" />
          <span dir="auto">{t('phoenixTracker.analysis.insights.empty')}</span>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Lightbulb className="w-4 h-4 text-app-primary-500" />
          <h3 className="text-sm font-semibold text-foreground" dir="auto">
            {t('phoenixTracker.analysis.insights.title')}
          </h3>
        </div>
        <ul className="space-y-2.5">
          {insights.map((ins, i) => {
            const Icon = ICON_BY_ID[ins.id] || Lightbulb
            const accent = ACCENT_BY_SEVERITY[ins.severity] || ACCENT_BY_SEVERITY.neutral
            return (
              <li key={i} className="flex items-start gap-2.5 text-sm" dir="auto">
                <Icon className={cn('w-4 h-4 flex-shrink-0 mt-0.5', accent)} />
                <p className="text-foreground leading-snug min-w-0">
                  <Trans
                    i18nKey={`phoenixTracker.analysis.insights.${ins.id}`}
                    values={ins.params}
                    components={INSIGHT_COMPONENTS}
                  />
                </p>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
